import axios, { type AxiosResponse } from 'axios'

import type {
    BoxTypesResponseSchema,
    CampaignsForShippingLabelsResponseSchema,
    CreateAdditionalShippingLabelPayloadSchema,
    CreateCarrierLabelPayloadSchema,
    CreateShippingLabelFromCampaignPayloadSchema,
    CreateShippingLabelFromEcommerceOrderPayloadSchema,
    CreateShippingLabelFromShippingOrderPayloadSchema,
    CountriesResponseSchema,
    EcommerceOrdersForShippingLabelsResponseSchema,
    PackageType,
    PackageTypesResponseSchema,
    PrintBulkSsccLabelsPayloadSchema,
    PrintShippingLabelPayloadSchema,
    PrintSsccLabelPayloadSchema,
    ShippingLabelDetails,
    ShippingLabelCreateResponse,
    ShippingLabelDestinationsResponseSchema,
    ShippingLabelPackageConfigurationPayloadSchema,
    ShippingLabelStatusesResponseSchema,
    ShippingLabelTemplatesResponseSchema,
    ShippingLabelTypesResponseSchema,
    ShippingLabelsListResponseSchema,
    ShippingOrdersForShippingLabelsResponseSchema,
    CustomerAddressesResponseSchema,
    UpdateShippingLabelGoodPayloadSchema,
    UpdateShippingLabelPayloadSchema
} from '@/api/schemas/shipments/shippingLabels'
import { API_ENDPOINTS } from '@/constants'

class ShippingLabelsQueries {
    private buildQuerySuffix(queryString = ''): string {
        if (!queryString) return ''
        return queryString.startsWith('?') ? queryString : `?${queryString}`
    }

    fetchShippingLabels(queryString = ''): Promise<AxiosResponse<ShippingLabelsListResponseSchema>> {
        return axios.get<ShippingLabelsListResponseSchema>(
            `${API_ENDPOINTS.SHIPPING_LABELS.LIST}${this.buildQuerySuffix(queryString)}`
        )
    }

    fetchShippingLabel(id: number | string): Promise<AxiosResponse<ShippingLabelDetails>> {
        return axios.get<ShippingLabelDetails>(`${API_ENDPOINTS.SHIPPING_LABELS.DETAILS}/${id}`)
    }

    createShippingLabelFromShippingOrder(
        payload: CreateShippingLabelFromShippingOrderPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelCreateResponse>> {
        return axios.post<ShippingLabelCreateResponse>(API_ENDPOINTS.SHIPPING_LABELS.CREATE_FROM_SHIPPING_ORDER, payload)
    }

    createShippingLabelFromEcommerceOrder(
        payload: CreateShippingLabelFromEcommerceOrderPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelCreateResponse>> {
        return axios.post<ShippingLabelCreateResponse>(API_ENDPOINTS.SHIPPING_LABELS.CREATE_FROM_ECOMMERCE_ORDER, payload)
    }

    createShippingLabelFromCampaign(
        payload: CreateShippingLabelFromCampaignPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelCreateResponse>> {
        return axios.post<ShippingLabelCreateResponse>(API_ENDPOINTS.SHIPPING_LABELS.CREATE_FROM_CAMPAIGN, payload)
    }

    updateShippingLabel(payload: UpdateShippingLabelPayloadSchema): Promise<AxiosResponse<ShippingLabelDetails>> {
        return axios.put<ShippingLabelDetails>(`${API_ENDPOINTS.SHIPPING_LABELS.DETAILS}/${payload.id}`, payload)
    }

    deleteShippingLabel(id: number): Promise<AxiosResponse<unknown>> {
        return axios.delete<unknown>(`${API_ENDPOINTS.SHIPPING_LABELS.DETAILS}/${id}`)
    }

    printShippingLabel(payload: PrintShippingLabelPayloadSchema): Promise<AxiosResponse<ArrayBuffer>> {
        return axios.post<ArrayBuffer>(API_ENDPOINTS.SHIPPING_LABELS.PRINT, payload, {
            responseType: 'arraybuffer'
        })
    }

    printSsccLabel(payload: PrintSsccLabelPayloadSchema): Promise<AxiosResponse<ArrayBuffer>> {
        return axios.post<ArrayBuffer>(API_ENDPOINTS.SHIPPING_LABELS.PRINT_SSCC, payload, {
            responseType: 'arraybuffer'
        })
    }

    printBulkSsccLabels(payload: PrintBulkSsccLabelsPayloadSchema): Promise<AxiosResponse<ArrayBuffer>> {
        return axios.post<ArrayBuffer>(API_ENDPOINTS.SHIPPING_LABELS.PRINT_SSCC_BULK, payload, {
            responseType: 'arraybuffer'
        })
    }

    createCarrierLabel(payload: CreateCarrierLabelPayloadSchema): Promise<AxiosResponse<ShippingLabelCreateResponse>> {
        return axios.post<ShippingLabelCreateResponse>(API_ENDPOINTS.SHIPPING_LABELS.CREATE_CARRIER_LABEL, payload)
    }

    createAdditionalShippingLabel(
        payload: CreateAdditionalShippingLabelPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelCreateResponse>> {
        return axios.post<ShippingLabelCreateResponse>(API_ENDPOINTS.SHIPPING_LABELS.CREATE_ADDITIONAL_LABEL, payload)
    }

    fetchShippingLabelTemplates(queryString = ''): Promise<AxiosResponse<ShippingLabelTemplatesResponseSchema>> {
        return axios.get<ShippingLabelTemplatesResponseSchema>(
            `${API_ENDPOINTS.SHIPPING_LABELS.TEMPLATES}${this.buildQuerySuffix(queryString)}`
        )
    }

    fetchShippingLabelStatuses(): Promise<AxiosResponse<ShippingLabelStatusesResponseSchema>> {
        return axios.get<ShippingLabelStatusesResponseSchema>(API_ENDPOINTS.SHIPPING_LABELS.STATUSES)
    }

    fetchShippingLabelTypes(): Promise<AxiosResponse<ShippingLabelTypesResponseSchema>> {
        return axios.get<ShippingLabelTypesResponseSchema>(API_ENDPOINTS.SHIPPING_LABELS.TYPES)
    }

    fetchShippingLabelDestinations(): Promise<AxiosResponse<ShippingLabelDestinationsResponseSchema>> {
        return axios.get<ShippingLabelDestinationsResponseSchema>(API_ENDPOINTS.SHIPPING_LABELS.DESTINATIONS)
    }

    updateShippingLabelGood(payload: UpdateShippingLabelGoodPayloadSchema): Promise<AxiosResponse<unknown>> {
        return axios.put<unknown>(`${API_ENDPOINTS.SHIPPING_LABELS.AVAILABLE_GOODS}/${payload.id}`, payload)
    }

    createShippingLabelPackageConfiguration(
        payload: ShippingLabelPackageConfigurationPayloadSchema
    ): Promise<AxiosResponse<unknown>> {
        return axios.post<unknown>(API_ENDPOINTS.SHIPPING_LABELS.PACKAGE_CONFIGURATIONS, payload)
    }

    updateShippingLabelPackageConfiguration(
        payload: ShippingLabelPackageConfigurationPayloadSchema
    ): Promise<AxiosResponse<unknown>> {
        return axios.put<unknown>(`${API_ENDPOINTS.SHIPPING_LABELS.PACKAGE_CONFIGURATIONS}/${payload.id}`, payload)
    }

    deleteShippingLabelPackageConfiguration(id: number): Promise<AxiosResponse<unknown>> {
        return axios.delete<unknown>(`${API_ENDPOINTS.SHIPPING_LABELS.PACKAGE_CONFIGURATIONS}/${id}`)
    }

    fetchShippingOrdersForShippingLabels(
        queryString = ''
    ): Promise<AxiosResponse<ShippingOrdersForShippingLabelsResponseSchema>> {
        return axios.get<ShippingOrdersForShippingLabelsResponseSchema>(
            `${API_ENDPOINTS.SHIPPING_ORDERS.FOR_SHIPPING_LABELS}${this.buildQuerySuffix(queryString)}`
        )
    }

    fetchOrdersForShippingLabels(queryString = ''): Promise<AxiosResponse<EcommerceOrdersForShippingLabelsResponseSchema>> {
        return axios.get<EcommerceOrdersForShippingLabelsResponseSchema>(
            `${API_ENDPOINTS.ORDERS.FOR_SHIPPING_LABELS}${this.buildQuerySuffix(queryString)}`
        )
    }

    fetchCampaignsForShippingLabel(queryString = ''): Promise<AxiosResponse<CampaignsForShippingLabelsResponseSchema>> {
        return axios.get<CampaignsForShippingLabelsResponseSchema>(
            `${API_ENDPOINTS.CAMPAIGNS.FOR_SHIPPING_LABEL}${this.buildQuerySuffix(queryString)}`
        )
    }

    fetchBoxTypes(): Promise<AxiosResponse<BoxTypesResponseSchema>> {
        return axios.get<BoxTypesResponseSchema>(API_ENDPOINTS.PACKAGE_DEFINITIONS.BOX_TYPES)
    }

    fetchPackageTypes(queryString = ''): Promise<AxiosResponse<PackageTypesResponseSchema>> {
        return axios.get<PackageTypesResponseSchema>(
            `${API_ENDPOINTS.PACKAGE_DEFINITIONS.PACKAGE_TYPES}${this.buildQuerySuffix(queryString)}`
        )
    }

    fetchCustomerAddresses(queryString = ''): Promise<AxiosResponse<CustomerAddressesResponseSchema>> {
        return axios.get<CustomerAddressesResponseSchema>(
            `${API_ENDPOINTS.CUSTOMERS.ADDRESSES}${this.buildQuerySuffix(queryString)}`
        )
    }

    fetchCountries(): Promise<AxiosResponse<CountriesResponseSchema>> {
        return axios.get<CountriesResponseSchema>(API_ENDPOINTS.GEOLOCALIZATIONS.COUNTRIES)
    }
}

export default new ShippingLabelsQueries()
